import type { FC } from 'react';
import { Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import type { KpiOption } from '../../../PlanDetail/MetricPage/config/kpiCatalog';
import type { DeltaColors } from '../../../config/scenarioComparisonConfig';
import { tpoPartTestId, tpoScenarioCellTestId, tpoScenarioColTestId } from '../../../config/testIds';
import formatKpiValue from '../../../utils/formatKpiValue';
import DeltaIndicator from './DeltaIndicator';

interface ScenarioColumnProps {
    planName: string;
    isBase?: boolean;
    basePrefix?: string;
    kpis: KpiOption[];
    kpiValues: Record<string, number | string | null>;
    kpiDeltas?: Record<string, number | null>;
    highlightedKpiKey?: string | null;
    onHighlightChange?: (kpiKey: string | null) => void;
    onRemove?: () => void;
    showPercent?: boolean;
    headerHeight?: number;
    rowHeight?: number;
    minWidth?: number;
    deltaColors: DeltaColors;
    stickyHeader?: boolean;
    /** Comparison plan id, or `SCENARIO_BASE_COLUMN_KEY` for the base column. */
    columnKey: string;
}

const ScenarioColumn: FC<ScenarioColumnProps> = ({
    planName,
    isBase,
    basePrefix = 'Base',
    kpis,
    kpiValues,
    kpiDeltas,
    highlightedKpiKey,
    onHighlightChange,
    onRemove,
    showPercent,
    headerHeight = 54,
    rowHeight = 38,
    minWidth = 200,
    deltaColors,
    stickyHeader,
    columnKey,
}) => {
    const columnTestId = tpoScenarioColTestId(columnKey);

    return (
        <div className="flex flex-1 flex-col border border-[#F0F0F0] rounded-lg" style={{ minWidth }}>
            <div
                className={`flex items-center gap-2 px-4 py-3 bg-[#FAFAFA] border-b border-[#F0F0F0] rounded-t-lg${
                    stickyHeader ? ' sticky top-0 z-10' : ''
                }`}
                style={{ height: headerHeight }}
                data-testid={columnTestId}
            >
                <Tooltip title={isBase ? `${basePrefix}: ${planName}` : planName}>
                    <span className="flex-1 font-medium text-sm truncate text-[#18181B]">
                        {isBase ? `${basePrefix}: ${planName}` : planName}
                    </span>
                </Tooltip>
                {onRemove ? (
                    <CloseOutlined
                        className="text-xs cursor-pointer text-[#A49FA9] hover:text-[#18181B] shrink-0"
                        onClick={onRemove}
                        data-testid={tpoPartTestId(columnTestId, 'remove_btn')}
                    />
                ) : null}
            </div>
            {kpis.map((kpi) => {
                const raw = kpiValues[kpi.key];
                const hasValue = raw != null;
                const isHighlighted = kpi.key === highlightedKpiKey;

                const delta = isBase ? null : kpiDeltas?.[kpi.key] ?? null;

                return (
                    <div
                        key={kpi.key}
                        className={`flex items-center justify-center px-4 border-b border-[#F0F0F0] text-sm cursor-pointer select-none last:border-b-0 last:rounded-b-lg ${
                            isHighlighted ? 'bg-[#F5F3FF]' : 'hover:bg-[#FAFAFA]'
                        }`}
                        style={{ height: rowHeight }}
                        onClick={() => onHighlightChange?.(kpi.key === highlightedKpiKey ? null : kpi.key)}
                        data-testid={tpoScenarioCellTestId(columnKey, kpi.key)}
                    >
                        {hasValue ? (
                            <div className="flex items-center gap-2">
                                <span>{formatKpiValue(raw)}</span>
                                <DeltaIndicator delta={delta} isPercent={showPercent} colors={deltaColors} />
                            </div>
                        ) : (
                            <span className="text-[#D9D9D9]">-</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

ScenarioColumn.displayName = 'ScenarioColumn';
export default ScenarioColumn;
